import Box from '@mui/material/Box';

import useScreenSize from '../../../util/hooks/useScreenSize';
import { convertNestedBlokKeys } from '../../../util/storyblok';

// Storyblok device buckets -> MUI breakpoint keys.
// useScreenSize thresholds (mobile <900, tablet 900–1199, desktop ≥1200) line up
// exactly with the theme breakpoints md=900 / lg=1200, so xs/md/lg reproduce the
// same behaviour — but as real CSS media queries that render correctly on the
// server for every viewport (E12), instead of one JS-selected breakpoint.
const DEVICE_TO_BP = { mobile: 'xs', tablet: 'md', desktop: 'lg' };

const responsiveValue = (style, prop, transform = (v) => v) => {
  const out = {};
  for (const [device, bp] of Object.entries(DEVICE_TO_BP)) {
    const value = transform(style?.[device]?.[prop]);
    if (value != null && value !== '') out[bp] = value;
  }
  return Object.keys(out).length ? out : undefined;
};

// Responsive spacing as MUI `sx` — spread by each component into its own `sx`.
export const buildBaseStyles = (style) => {
  const sx = {};
  const padding = responsiveValue(style, 'padding');
  const margin = responsiveValue(style, 'margin');
  if (padding) sx.padding = padding;
  if (margin) sx.margin = margin;
  return sx;
};

// The Storyblok "alignment" field (Left=flex-start / Center=center / Right=flex-end)
// is the HORIZONTAL alignment of the whole block. It is applied via `alignItems`
// on a flex-column wrapper around the component, which works for every component
// regardless of its own markup. We only wrap when an alignment actually moves the
// block (center/flex-end) so the default "Left" blocks render unchanged.
export const buildAlignItems = (style) => {
  const align = responsiveValue(style, 'alignment', (v) => (v && v !== 'none' ? v : undefined));
  if (!align) return undefined;
  const moves = Object.values(align).some((v) => v === 'center' || v === 'flex-end');
  return moves ? align : undefined;
};

export const withBaseComponent = (Component) => {
  return ({ blok, ...restProps }) => {
    const convertedBlok = convertNestedBlokKeys(blok);
    const { responsive, style, ...restBlok } = convertedBlok;

    const { isDesktop, isTablet, isMobile } = useScreenSize();

    // Per-breakpoint visibility stays JS-driven (return null) — useScreenSize is
    // already SSR-safe via deviceType, and a CSS display toggle would clash with
    // each component's own display value. Optional chaining guards partial data.
    if (responsive) {
      if (isDesktop && responsive.desktop?.hidden) return null;
      if (isTablet && responsive.tablet?.hidden) return null;
      if (isMobile && responsive.mobile?.hidden) return null;
    }

    const alignItems = buildAlignItems(style);
    const baseStyles = buildBaseStyles(style);

    // Flex/Grid components (grid_item, grid_section) place alignItems in their own
    // flex `sx` (centring their children) — wrapping them in a Box would break the
    // MUI Grid parent/child relationship. They opt out via `handlesAlignItems`.
    if (alignItems && Component.handlesAlignItems) baseStyles.alignItems = alignItems;

    const inner = <Component baseStyles={baseStyles} blok={restBlok} {...restProps} />;

    if (!alignItems || Component.handlesAlignItems) return inner;

    // Content blocks: a flex-column wrapper aligns the whole block horizontally.
    return <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems }}>{inner}</Box>;
  };
};
